/**
 * ScreenContext v1 — what the creator is looking at. Mirrors lighthouse-backend `app/harness/screen.py`.
 * Sent with every agent message so "this draft", "these" and "the one on screen" resolve
 * deterministically on the server.
 */

export type ScreenEntityType =
    | 'thread' | 'project' | 'deal' | 'deliverable' | 'task' | 'invoice' | 'company' | 'contact'
    | 'agreement' | 'draft' | 'pitch' | 'outreach' | 'file' | 'script' | 'checkin' | 'goal'

export interface ScreenEntity {
    type: ScreenEntityType | string
    id: string
    label?: string
    primary?: boolean
}

export interface ScreenSelection {
    type: ScreenEntityType | string
    ids: string[]
    total?: number
    labels?: string[]
}

export interface ScreenView {
    key: string
    label?: string
    filters?: Record<string, unknown>
}

export interface ScreenComposer {
    draft_id?: string | null
    thread_id?: string | null
    body?: string
    selection_text?: string
    dirty?: boolean
}

export interface ScreenVisible {
    type: ScreenEntityType | string
    ids: string[]
}

export interface ScreenUnsaved {
    type: string
    id: string
    fields: string[]
}

export interface ScreenContext {
    v: 1
    route: string
    page: string
    entities: ScreenEntity[]
    selection?: ScreenSelection
    view?: ScreenView
    composer?: ScreenComposer
    visible?: ScreenVisible
    unsaved?: ScreenUnsaved[]
}

/** A view's contribution. Later (more recently mounted) frames win for single-valued fields. */
export interface ScreenFrame {
    page?: string
    entities?: ScreenEntity[]
    selection?: ScreenSelection | null
    view?: ScreenView | null
    composer?: ScreenComposer | null
    visible?: ScreenVisible | null
    unsaved?: ScreenUnsaved[]
}

export const LIMITS = {
    entities: 40,
    selectionIds: 1000,
    visibleIds: 50,
    composerBody: 20_000,
    unsaved: 40,
} as const
